import { type HTMLAttributes, useState } from 'react';

import styles from './Pagination.module.css';
import { PaginationGenerator } from './paginationGenerator';
import { useControllableState } from '../../hooks/useControllableState';
import { createTexts, useTranslation } from '../../i18n';
import { commonTexts } from '../../i18n/commonTexts';
import { type BaseComponentProps, getBaseHTMLProps } from '../../types';
import { Button } from '../Button';
import utilStyles from '../helpers/styling/utilStyles.module.css';
import { Icon } from '../Icon';
import {
  ChevronFirstIcon,
  ChevronLastIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from '../Icon/icons';
import {
  Box,
  type BoxProps,
  type Breakpoint,
  Grid,
  type GridProps,
} from '../layout';
import { styleUpToBreakpoint } from '../layout/common/utils';
import { Select } from '../Select';
import { Paragraph } from '../Typography';

export interface PaginationOption {
  label: string;
  value: number;
}

export type PaginationProps = BaseComponentProps<
  HTMLElement,
  {
    /**Totalt antall elementer som skal pagineres. */
    itemsAmount: number;
    /**Antall elementer per side ved innlastning.
     * @default 10
     */
    defaultItemsPerPage?: number;
    /**Den aktive siden ved innlastning.
     * @default 1
     */
    defaultActivePage?: number;
    /**Den aktive siden. Brukes til kontrollert tilstand - når denne er satt styrer du siden utenfra. */
    activePage?: number;
    /**Spesifiserer om selve pagineringen skal vises.
     * @default true
     */
    withPagination?: boolean;
    /**Om teksten `'Vis x-y av z'` skal vises. */
    withCounter?: boolean;
    /**Om `<Select>` for å velge antall per side skal vises. */
    withSelect?: boolean;
    /**Custom options for `<Select>`. **OBS!** husk å inkludere "Alle"-alternativet hvis relevant - brukere forventer ofte den muligheten.
     * @default [
        { label: '10', value: 10 },
        { label: '25', value: 25 },
        { label: '50', value: 50 },
        { label: 'Alle', value: itemsAmount },
      ]
     */
    selectOptions?: Array<PaginationOption>;
    /**Kalles ved sideendring - henter ny aktiv side og kjører ekstra logikk. */
    onChange?: (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      page: number,
    ) => void;
    /**Kalles når `selectedOption` endres, hvis `withSelect="true"`.
     * Brukes til å hente valgt alternativ og evt. kjøre ekstra logikk.
     */
    onSelectOptionChange?: (option: PaginationOption | null) => void;
    /**Brekkpunkt for små skjermer; den viser færre sideknapper og stacker delkomponentene. */
    smallScreenBreakpoint?: Breakpoint;
  },
  Omit<HTMLAttributes<HTMLElement>, 'onChange'>
>;

export const Pagination = ({
  itemsAmount,
  defaultItemsPerPage = 10,
  defaultActivePage = 1,
  activePage: activePageProp,
  withPagination = true,
  withCounter,
  withSelect,
  selectOptions,
  smallScreenBreakpoint,
  onChange,
  onSelectOptionChange,
  id,
  className,
  style,
  htmlProps,
  ref,
  ...rest
}: PaginationProps) => {
  const { t } = useTranslation();

  const tSelectOptions =
    selectOptions && selectOptions.length > 0
      ? selectOptions
      : [
          { label: '10', value: 10 },
          { label: '25', value: 25 },
          { label: '50', value: 50 },
          { label: t(texts.all), value: itemsAmount },
        ];

  if (
    withSelect &&
    !tSelectOptions.some(o => o.value === defaultItemsPerPage)
  ) {
    console.warn(
      `[Pagination] defaultItemsPerPage prop value (${defaultItemsPerPage}) is not included in customOptions prop. Please add it to ensure it appears in the dropdown.`,
    );
  }
  const [activePage, setActivePage] = useControllableState({
    value: activePageProp,
    defaultValue: defaultActivePage,
  });
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  const pagesLength = Math.ceil(itemsAmount / itemsPerPage);

  const items = PaginationGenerator(pagesLength, activePage);

  const onPageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    page: number,
  ) => {
    if (page) setActivePage(page);
    if (event && onChange) {
      onChange(event, page);
    }
  };

  const handleSelectChange = (option: PaginationOption | null) => {
    if (option !== null) {
      setItemsPerPage(option.value);
    }

    if (onSelectOptionChange) {
      onSelectOptionChange(option);
    }
  };

  const listItemProps: Partial<BoxProps<'li'>> = {
    as: 'li',
    display: 'inline-grid',
    alignContent: 'center',
  };

  const listProps: Partial<GridProps<'ol'>> = {
    as: 'ol',
    defaultPageLayout: false,
    gridAutoFlow: 'column',
    margin: 'x0',
    padding: 'x0',
    gap: 'x0.75',
  };

  const listItems =
    items.length > 0
      ? items.map((item, i) => {
          const isActive = item === activePage;
          return (
            <Box {...listItemProps} key={`pagination-item-${i}`}>
              {item !== 'truncator' && typeof item === 'number' ? (
                <Button
                  purpose={isActive ? 'primary' : 'secondary'}
                  size="small"
                  onClick={event => {
                    onPageChange(event, item);
                  }}
                  aria-label={
                    isActive
                      ? t(commonTexts.currentPage(item))
                      : t(commonTexts.page(item))
                  }
                >
                  {item}
                </Button>
              ) : (
                <Icon
                  icon={MoreHorizontalIcon}
                  className={styles['truncation-icon']}
                />
              )}
            </Box>
          );
        })
      : undefined;

  const previousPageButton = (
    <Button
      purpose="secondary"
      size="small"
      icon={ChevronLeftIcon}
      onClick={event => {
        onPageChange(event, activePage - 1);
      }}
      aria-label={t(commonTexts.previousPage)}
    />
  );

  const nextPageButton = (
    <Button
      purpose="secondary"
      size="small"
      icon={ChevronRightIcon}
      onClick={event => {
        onPageChange(event, activePage + 1);
      }}
      aria-label={t(commonTexts.nextPage)}
    />
  );

  const isOnFirstPage = activePage === 1;
  const isOnLastPage = activePage === pagesLength;
  const baseHTMLProps = getBaseHTMLProps(id, className, style, htmlProps, rest);

  const navigation = withPagination ? (
    <Box
      as="nav"
      ref={ref}
      aria-label={t(texts.pagination)}
      display="flex"
      alignItems="center"
      {...(!withSelect &&
        !withCounter && {
          ...baseHTMLProps,
        })}
    >
      <Grid {...listProps} hideBelow={smallScreenBreakpoint}>
        <Box
          {...listItemProps}
          className={isOnFirstPage ? utilStyles.invisible : undefined}
          aria-hidden={isOnFirstPage}
        >
          {previousPageButton}
        </Box>
        {listItems}
        <Box
          {...listItemProps}
          className={isOnLastPage ? utilStyles.invisible : undefined}
          aria-hidden={isOnLastPage}
        >
          {nextPageButton}
        </Box>
      </Grid>
      {!!smallScreenBreakpoint && (
        <Grid {...listProps} showBelow={smallScreenBreakpoint}>
          <Box
            {...listItemProps}
            className={isOnFirstPage ? utilStyles.invisible : undefined}
            aria-hidden={isOnFirstPage}
          >
            <Button
              purpose="secondary"
              size="small"
              icon={ChevronFirstIcon}
              onClick={event => {
                onPageChange(event, 1);
              }}
              aria-label={t(texts.firstPage)}
            />
          </Box>
          <Box
            {...listItemProps}
            className={isOnFirstPage ? utilStyles.invisible : undefined}
            aria-hidden={isOnFirstPage}
          >
            {previousPageButton}
          </Box>
          <Box {...listItemProps}>
            <Button
              size="small"
              onClick={event => {
                onPageChange(event, activePage);
              }}
            >
              {activePage}
            </Button>
          </Box>
          <Box
            {...listItemProps}
            className={isOnLastPage ? utilStyles.invisible : undefined}
            aria-hidden={isOnLastPage}
          >
            {nextPageButton}
          </Box>
          <Box
            {...listItemProps}
            className={isOnLastPage ? utilStyles.invisible : undefined}
            aria-hidden={isOnLastPage}
          >
            <Button
              purpose="secondary"
              size="small"
              icon={ChevronLastIcon}
              onClick={event => {
                onPageChange(event, pagesLength);
              }}
              aria-label={t(texts.lastPage)}
            />
          </Box>
        </Grid>
      )}
    </Box>
  ) : null;

  const activePageFirstItem =
    activePage === 1 ? 1 : activePage * itemsPerPage - itemsPerPage + 1;

  const activePageLastItem =
    activePage === pagesLength ? itemsAmount : activePage * itemsPerPage;

  return !withCounter && !withSelect ? (
    navigation
  ) : (
    <Box
      display="flex"
      gap="x0.5"
      justifyContent="space-between"
      flexWrap="wrap"
      flexDirection={styleUpToBreakpoint('column', smallScreenBreakpoint)}
      alignItems={styleUpToBreakpoint('center', smallScreenBreakpoint)}
      {...baseHTMLProps}
    >
      <Grid
        defaultPageLayout={false}
        gap="x0.5"
        alignItems="center"
        gridAutoFlow="column"
      >
        {withSelect && (
          <Select
            options={tSelectOptions}
            isSearchable={false}
            width="90px"
            defaultValue={{
              label: itemsPerPage.toString(),
              value: itemsPerPage,
            }}
            isClearable={false}
            onChange={handleSelectChange}
            componentSize="small"
            aria-label={t(texts.itemsPerPage)}
          />
        )}
        {withCounter && (
          <Paragraph typographyType="bodyShortMedium">
            {t(
              texts.showsAmountOfTotalItems(
                activePageFirstItem,
                activePageLastItem,
                itemsAmount,
              ),
            )}
          </Paragraph>
        )}
      </Grid>
      {navigation}
    </Box>
  );
};

Pagination.displayName = 'Pagination';

const texts = createTexts({
  pagination: {
    nb: 'Paginering',
    no: 'Paginering',
    nn: 'Paginering',
    en: 'Pagination',
    se: 'Pagineren',
  },
  itemsPerPage: {
    nb: 'Elementer per side',
    no: 'Elementer per side',
    nn: 'Element per side',
    en: 'Items per page',
    se: 'Elementat juohki siidui',
  },
  firstPage: {
    nb: 'Første side',
    no: 'Første side',
    nn: 'Fyrste side',
    en: 'First page',
    se: 'Vusttaš siidu',
  },
  lastPage: {
    nb: 'Siste side',
    no: 'Siste side',
    nn: 'Siste side',
    en: 'Last page',
    se: 'Maŋimuš siidu',
  },
  showsAmountOfTotalItems: (first, last, total) => ({
    nb: `Viser ${first}-${last} av ${total}`,
    no: `Viser ${first}-${last} av ${total}`,
    nn: `Viser ${first}-${last} av ${total}`,
    en: `Shows ${first}-${last} of ${total}`,
    se: `Čájeha ${first}-${last} ${total} gaskkas`,
  }),
  all: {
    nb: 'Alle',
    no: 'Alle',
    nn: 'Alle',
    en: 'All',
    se: 'Buot',
  },
});
